import { useRef, useState, useCallback } from 'react';
import type React from 'react';
import { applyFontSize, rgbToHex } from '../utils/dom';
import { FONT_FAMILIES, FONT_SIZES } from '../constants';

const DEFAULT_FONT_FAMILY = '"Times New Roman", serif';

/**
 * Owns all formatting state and the execCommand-based editing API.
 * Provides stable callbacks for the toolbar and color pickers.
 */
export function useEditorCommands(iframeRef: React.RefObject<HTMLIFrameElement>) {
  const savedRange = useRef<Range | null>(null);
  const activeCellRef = useRef<HTMLTableCellElement | null>(null);

  const [fmt, setFmt] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    justifyFull: false,
  });
  const [textColor, setTextColorState] = useState('#000000');
  const [hlColor, setHlColorState] = useState('#ffff00');
  const [fontFamily, setFontFamily] = useState(DEFAULT_FONT_FAMILY);
  const [fontSize, setFontSize] = useState('');
  const [cellBgColor, setCellBgColor] = useState('#ffffff');
  const [inTable, setInTable] = useState(false);

  const iDoc = useCallback(() => iframeRef.current?.contentDocument ?? null, [iframeRef]);
  const iWin = useCallback(() => iframeRef.current?.contentWindow ?? null, [iframeRef]);

  const normalizeColor = useCallback((raw: string | null | undefined, fallback: string) => {
    if (!raw) return fallback;
    const normalized = raw.trim().toLowerCase();
    if (
      !normalized ||
      normalized === 'initial' ||
      normalized === 'inherit' ||
      normalized === 'transparent' ||
      normalized === 'rgba(0, 0, 0, 0)'
    ) {
      return fallback;
    }
    if (normalized.startsWith('#')) return normalized;
    if (normalized.startsWith('rgb')) return rgbToHex(normalized);
    return fallback;
  }, []);

  const getToolbarFontFamily = useCallback((rawFontFamily: string | null | undefined) => {
    if (!rawFontFamily) return DEFAULT_FONT_FAMILY;
    const normalizedRaw = rawFontFamily.toLowerCase();
    const match = FONT_FAMILIES.find(option => normalizedRaw.includes(option.label.toLowerCase()));
    return match?.value ?? DEFAULT_FONT_FAMILY;
  }, []);

  const getToolbarFontSize = useCallback((rawFontSize: string | null | undefined) => {
    if (!rawFontSize) return '';
    const px = parseFloat(rawFontSize);
    if (!Number.isFinite(px) || px <= 0) return '';
    const pt = (px * 72) / 96;
    const nearest = FONT_SIZES.reduce((closest, candidate) => {
      return Math.abs(candidate - pt) < Math.abs(closest - pt) ? candidate : closest;
    }, FONT_SIZES[0]);
    return String(nearest);
  }, []);

  /**
   * Returns true only when every text node in the range has underline applied.
   * queryCommandState('underline') returns true even for partial selections, so
   * this is used for accurate toolbar state and toggle direction.
   */
  const isFullyUnderlined = useCallback(
    (range: Range): boolean => {
      const win = iWin();
      if (!win) return false;

      const root = range.commonAncestorContainer;

      const textNodes: Text[] = [];
      if (root.nodeType === Node.TEXT_NODE) {
        const start = range.startOffset;
        const end = range.endOffset;
        if (end > start) textNodes.push(root as Text);
      } else {
        const treeWalker = (win.document ?? iDoc()!).createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
        let node = treeWalker.nextNode();
        while (node) {
          if (range.intersectsNode(node)) {
            const start = node === range.startContainer ? range.startOffset : 0;
            const end = node === range.endContainer ? range.endOffset : (node as Text).data.length;
            if (end > start) textNodes.push(node as Text);
          }
          node = treeWalker.nextNode();
        }
      }

      if (textNodes.length === 0) return false;

      return textNodes.every(tn => {
        let el: Element | null = tn.parentElement;
        while (el && el.nodeName !== 'BODY') {
          if (el.nodeName === 'U') return true;
          if (win.getComputedStyle(el).textDecorationLine.includes('underline')) return true;
          el = el.parentElement;
        }
        return false;
      });
    },
    [iDoc, iWin],
  );

  /** Pull current `queryCommandState` values and update caret-context state. */
  const syncState = useCallback(() => {
    const doc = iDoc();
    const win = iWin();
    if (!doc || !win) return;
    try {
      const sel = win.getSelection();
      const range = sel?.rangeCount ? sel.getRangeAt(0) : null;
      // For underline, queryCommandState returns true even on partial selections.
      // Only show the button as active when the entire selection is underlined.
      const underlineActive = range && !range.collapsed ? isFullyUnderlined(range) : doc.queryCommandState('underline');

      setFmt({
        bold: doc.queryCommandState('bold'),
        italic: doc.queryCommandState('italic'),
        underline: underlineActive,
        strikethrough: doc.queryCommandState('strikeThrough'),
        justifyLeft: doc.queryCommandState('justifyLeft'),
        justifyCenter: doc.queryCommandState('justifyCenter'),
        justifyRight: doc.queryCommandState('justifyRight'),
        justifyFull: doc.queryCommandState('justifyFull'),
      });

      if (sel?.rangeCount) {
        const startNode = sel.getRangeAt(0).startContainer;
        let node: Element | null = startNode.nodeType === Node.TEXT_NODE ? (startNode as Text).parentElement : (startNode as Element);
        let cell: HTMLTableCellElement | null = null;
        while (node && node.nodeName !== 'HTML') {
          if (node.nodeName === 'TD' || node.nodeName === 'TH') {
            cell = node as HTMLTableCellElement;
            break;
          }
          node = node.parentElement;
        }
        activeCellRef.current = cell;
        setInTable(!!cell);
        if (cell) setCellBgColor(cell.style.backgroundColor ? rgbToHex(cell.style.backgroundColor) : '#ffffff');

        const styleNode =
          (startNode.nodeType === Node.ELEMENT_NODE ? (startNode as Element) : (startNode.parentElement ?? node)) ?? doc.body;
        const computed = win.getComputedStyle(styleNode);
        setTextColorState(normalizeColor(computed.color, '#000000'));
        setHlColorState(normalizeColor(computed.backgroundColor, '#ffff00'));
        setFontFamily(getToolbarFontFamily(computed.fontFamily));
        setFontSize(getToolbarFontSize(computed.fontSize));
      }
    } catch {
      // selection API can throw on cross-origin or detached frames
    }
  }, [iDoc, iWin, isFullyUnderlined, normalizeColor, getToolbarFontFamily, getToolbarFontSize]);

  /** Snapshot the current selection so toolbar interactions don't lose it. */
  const saveSelection = useCallback(() => {
    const sel = iWin()?.getSelection();
    savedRange.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
  }, [iWin]);

  /** After execCommand mutates the DOM the cloned range may be stale — refresh it. */
  const refreshSavedSelection = useCallback(() => {
    const sel = iWin()?.getSelection();
    if (!sel?.rangeCount) return;
    try {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    } catch {
      /* ignore */
    }
  }, [iWin]);

  /** Restore a previously saved selection (used after color picker / dropdown focus). */
  const restoreSelection = useCallback(() => {
    const sel = iWin()?.getSelection();
    if (!sel || !savedRange.current) return;
    try {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    } catch {
      savedRange.current = null;
    }
  }, [iWin]);

  /**
   * Mutate underline on the selection. Wraps selection in <u> to apply, or
   * extracts+strips to remove. Uses a wrapper span as a stable re-selection anchor.
   */
  const toggleUnderline = useCallback((range: Range, sel: Selection, doc: Document, win: Window, apply: boolean) => {
    const anchor = doc.createElement('span');
    anchor.id = '__ul_anchor__';
    try {
      range.surroundContents(anchor);
    } catch {
      // surroundContents fails when the range partially overlaps an element boundary.
      // Fall back to extract+insert.
      anchor.appendChild(range.extractContents());
      range.insertNode(anchor);
    }

    // Strip all underline from inside the anchor
    for (const u of Array.from(anchor.querySelectorAll('u'))) {
      const p = u.parentNode!;
      while (u.firstChild) p.insertBefore(u.firstChild, u);
      p.removeChild(u);
    }
    for (const el of Array.from(anchor.querySelectorAll<HTMLElement>('[style]'))) {
      el.style.removeProperty('text-decoration');
      el.style.removeProperty('text-decoration-line');
      if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
    }

    if (!apply) {
      let ancestor = anchor.parentElement;
      while (ancestor && ancestor.nodeName !== 'BODY') {
        if (ancestor.nodeName === 'U') {
          const uEl = ancestor;
          const uParent = uEl.parentNode!;
          const beforeFrag = doc.createDocumentFragment();
          const afterFrag = doc.createDocumentFragment();
          let reachedAnchor = false;
          for (const child of Array.from(uEl.childNodes)) {
            if (child === anchor) {
              reachedAnchor = true;
              continue;
            }
            if (!reachedAnchor) beforeFrag.appendChild(child);
            else afterFrag.appendChild(child);
          }
          if (beforeFrag.hasChildNodes()) {
            const beforeU = doc.createElement('u');
            beforeU.appendChild(beforeFrag);
            uParent.insertBefore(beforeU, uEl);
          }
          uParent.insertBefore(anchor, uEl);
          if (afterFrag.hasChildNodes()) {
            const afterU = doc.createElement('u');
            afterU.appendChild(afterFrag);
            uParent.insertBefore(afterU, uEl);
          }
          uParent.removeChild(uEl);
          break;
        }
        ancestor = ancestor.parentElement;
      }
      ancestor = anchor.parentElement;
      while (ancestor && ancestor.nodeName !== 'BODY') {
        const ancestorEl = ancestor as HTMLElement;
        const computedDecoration = win.getComputedStyle(ancestorEl).textDecorationLine;
        if (computedDecoration.includes('underline')) {
          ancestorEl.style.setProperty('text-decoration', 'none');
          const children = Array.from(ancestorEl.childNodes);
          let i = 0;
          while (i < children.length) {
            if (children[i] === anchor) {
              i++;
              continue;
            }
            const run: ChildNode[] = [];
            while (i < children.length && children[i] !== anchor) {
              run.push(children[i]);
              i++;
            }
            if (run.length > 0) {
              const wrapU = doc.createElement('u');
              ancestorEl.insertBefore(wrapU, run[0]);
              for (const n of run) wrapU.appendChild(n);
            }
          }
          break;
        }
        ancestor = ancestor.parentElement;
      }
    }

    if (apply) {
      const u = doc.createElement('u');
      while (anchor.firstChild) u.appendChild(anchor.firstChild);
      anchor.appendChild(u);
    }

    const restored = doc.createRange();
    restored.selectNodeContents(anchor);
    sel.removeAllRanges();
    sel.addRange(restored);

    const parent = anchor.parentNode!;
    while (anchor.firstChild) parent.insertBefore(anchor.firstChild, anchor);
    parent.removeChild(anchor);
  }, []);

  /** Run a `document.execCommand` then sync toolbar state. */
  const exec = useCallback(
    (cmd: string, value: string | null = null) => {
      const doc = iDoc();
      const win = iWin();
      if (!doc) return;

      if (cmd === 'underline') {
        const sel = win?.getSelection();
        if (win && sel?.rangeCount) {
          const range = sel.getRangeAt(0);
          const apply = !isFullyUnderlined(range);
          toggleUnderline(range, sel, doc, win, apply);
          refreshSavedSelection();
          syncState();
          return;
        }
      }

      doc.execCommand(cmd, false, value ?? undefined);
      refreshSavedSelection();
      syncState();
    },
    [iDoc, iWin, isFullyUnderlined, toggleUnderline, refreshSavedSelection, syncState],
  );

  /**
   * Apply a point-size font to the current selection. Uses a workaround because
   * `execCommand('fontSize')` only accepts 1-7, not arbitrary point values.
   */
  const applyFontSizeFromToolbar = useCallback(
    (sizePt: number) => {
      const doc = iDoc();
      if (!doc) return;
      applyFontSize(doc, sizePt);
      refreshSavedSelection();
      syncState();
    },
    [iDoc, refreshSavedSelection, syncState],
  );

  return {
    exec,
    syncState,
    saveSelection,
    restoreSelection,
    applyFontSizeFromToolbar,
    fmt,
    textColor,
    setTextColorState,
    hlColor,
    setHlColorState,
    fontFamily,
    fontSize,
    cellBgColor,
    setCellBgColor,
    inTable,
    setInTable,
    activeCellRef,
  };
}
